import type { LoaderFunctionArgs, SerializeFrom } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useParams } from "@remix-run/react";

import { useFetch } from "~/hooks/useFetch";
import { createServerStateManager, ServerStateManager } from "~/modules/StateManager.server";
import { getOffices, SchedulingRulesOfficeResponse } from "~/services/schedulingRules.server";
import { parseMarkdown } from "~/utils/parseMarkdown.server";

export interface OfficeApiLoaderData extends SerializeFrom<typeof loader> {}

export async function loader(args: LoaderFunctionArgs) {
  const stateManager = await createServerStateManager(args);

  const data = await getOfficeLoaderData(stateManager);

  stateManager.dispatchCAEvent({
    type: "data-shown",
    payload: { subtype: "office", officeIds: data.offices.map((office) => office.id) },
  });

  return json(data);
}

export function useOfficeApiFetcher() {
  const { enterprise } = useParams<"enterprise">();

  const fetcher = useFetch<OfficeApiLoaderData>(`/api/enterprises/${enterprise}/offices`);

  return fetcher;
}
export async function getOfficeLoaderData(stateManager: ServerStateManager) {
  const { state } = stateManager;
  const geolocation = state.location?.geolocation ?? state.config.location.geolocation;

  const srsParams = stateManager.getSRSParams();
  const data = await getOffices(srsParams);

  return {
    offices: await transformOffices(data),
    sort: {
      nearestToGeo: srsParams.params?.nearestToGeo,
    },
    location: {
      geolocation,
      formattedAddress: state.location?.formattedAddress,
      placeId: state.location?.placeId,
    },
  };
}

async function transformOffices(offices: Awaited<ReturnType<typeof getOffices>>) {
  return Promise.all(offices.map(pruneOffice));
}

async function pruneOffice({
  description,
  external_id: externalId,
  id,
  is_virtual: isVirtual,
  location,
  title,
}: SchedulingRulesOfficeResponse) {
  const parsedDescription = await parseMarkdown(description, { officeId: id });

  const distance = location?.distance ? Math.round(location.distance * 100) / 100 : undefined;

  return {
    id,
    externalId,
    title,
    description: parsedDescription,
    isVirtual,
    location: { address: location?.full_address, distance },
  };
}
